import { useMutation } from '@apollo/client/react'
import { Center, Spinner } from '@chakra-ui/react'
import { ChatThread, ChatThreadProps } from '@src/components/sco/Chat/ChatThread'
import { Paragraph } from '@src/components/sco/Paragraph'
import { UpsertDirectChatThreadInput } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import { useEffect, useMemo, useRef, useState } from 'react'

type DirectChatThreadAmongstUsersProps = {
  threadId?: string
  userIds?: string[]
  attachmentType?: string
  attachmentId?: string
  chatThreadProps?: ChatThreadProps
}

export const DirectChatThreadAmongstUsers = (props: DirectChatThreadAmongstUsersProps) => {
  const [threadId, setThreadId] = useState<string | null>(props.threadId ?? null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Stable key from member ids (sorted), not array identity — parent often passes a new
  // userIds array each render, which must not re-trigger the upsert effect.
  const memberUserIdsKey = useMemo(
    () =>
      Array.from(new Set((props.userIds ?? []).filter(Boolean)))
        .slice()
        .sort()
        .join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally depend on content
    [props.userIds?.join(',')]
  )

  const conversationKey = useMemo(
    () =>
      [props.threadId ?? '', memberUserIdsKey, props.attachmentType ?? '', props.attachmentId ?? ''].join(
        '|'
      ),
    [props.attachmentId, props.attachmentType, props.threadId, memberUserIdsKey]
  )

  const [upsertDirectChatThread] = useMutation(gql`
    mutation ($input: UpsertDirectChatThreadInput!) {
      upsertDirectChatThread(input: $input) {
        thread {
          id
          threadId
        }
        viewer {
          id
          currentUser {
            id
            remainingMemberChatsCount
          }
        }
      }
    }
  `)
  const upsertRef = useRef(upsertDirectChatThread)
  upsertRef.current = upsertDirectChatThread

  useEffect(() => {
    if (props.threadId) {
      setThreadId(props.threadId)
      setErrorMessage(null)
      return
    }

    setThreadId(null)
    setErrorMessage(null)

    if (!memberUserIdsKey) {
      return
    }

    // Dedupe so [self, self] becomes a valid single-member self-chat.
    const uniqueUserIds = memberUserIdsKey.split(',').filter(Boolean)
    if (!uniqueUserIds.length) {
      return
    }

    let cancelled = false

    const input: UpsertDirectChatThreadInput = {
      memberUserIds: uniqueUserIds,
      attachmentType: props.attachmentType,
      attachmentId: props.attachmentId,
    }

    upsertRef.current({ variables: { input } }).then(result => {
      if (cancelled) {
        return
      }
      if (result.errors) {
        setErrorMessage(result.errors[0].message)
        return
      }
      setThreadId(result.data.upsertDirectChatThread.thread.threadId)
    })

    return () => {
      cancelled = true
    }
    // Only re-run when the conversation identity changes — not on mutate fn identity.
  }, [conversationKey, memberUserIdsKey, props.attachmentId, props.attachmentType, props.threadId])

  if (errorMessage) {
    return (
      <Center padding={4}>
        <Paragraph textAlign={'center'}>{errorMessage}</Paragraph>
      </Center>
    )
  }

  if (!threadId) {
    return (
      <Center padding={4}>
        <Spinner />
      </Center>
    )
  }

  return <ChatThread key={threadId} threadId={threadId} {...props.chatThreadProps} />
}
